/**
 * Single source of truth for the admin area's structure: every admin page,
 * which of the 5 domain groups it belongs to, and the keywords the ⌘K
 * command palette matches it on. AdminShell (sidebar), AdminHomePage
 * (dashboard quick-access) and AdminCommandPalette all derive their listing
 * from this registry so the three stay in sync automatically.
 */

export type AdminPageGroup = "access" | "tickets" | "communication" | "ai" | "automation" | "system";

export type AdminPageEntry = {
  /** Also used as the React key and in `admin-nav-<slug>` / `admin-search-result-<slug>` testids. */
  slug: string;
  route: string;
  group: AdminPageGroup;
  nameKey: string;
  descriptionKey: string;
  /** German + English synonyms and setting-related terms the palette should also match. */
  keywords: string[];
};

export const ADMIN_PAGE_GROUPS: AdminPageGroup[] = [
  "access",
  "tickets",
  "communication",
  "ai",
  "automation",
  "system",
];

export const ADMIN_PAGES: AdminPageEntry[] = [
  // ── Zugriff & Benutzer ────────────────────────────────────────────────
  {
    slug: "users",
    route: "/admin/users",
    group: "access",
    nameKey: "admin.nav.users",
    descriptionKey: "admin.pageDescriptions.users",
    keywords: ["agent", "mitarbeiter", "benutzer", "account", "login", "employee"],
  },
  {
    slug: "groups",
    route: "/admin/groups",
    group: "access",
    nameKey: "admin.nav.groups",
    descriptionKey: "admin.pageDescriptions.groups",
    keywords: ["berechtigung", "permissions", "gruppen", "access"],
  },
  {
    slug: "roles",
    route: "/admin/roles",
    group: "access",
    nameKey: "admin.nav.roles",
    descriptionKey: "admin.pageDescriptions.roles",
    keywords: ["rollen", "rbac", "berechtigung", "permissions"],
  },
  {
    slug: "agent-roles",
    route: "/admin/agent-roles",
    group: "access",
    nameKey: "admin.nav.agentRoles",
    descriptionKey: "admin.pageDescriptions.agentRoles",
    keywords: ["zuordnung", "assignment", "rollenzuordnung", "agent roles"],
  },
  {
    slug: "agent-groups",
    route: "/admin/agent-groups",
    group: "access",
    nameKey: "admin.nav.agentGroups",
    descriptionKey: "admin.pageDescriptions.agentGroups",
    keywords: ["zuordnung", "assignment", "gruppenzuordnung", "agent groups"],
  },
  {
    slug: "role-groups",
    route: "/admin/role-groups",
    group: "access",
    nameKey: "admin.nav.roleGroups",
    descriptionKey: "admin.pageDescriptions.roleGroups",
    keywords: ["rollen", "gruppen", "zuordnung", "role groups", "assignment"],
  },
  {
    slug: "customer-users",
    route: "/admin/customer-users",
    group: "access",
    nameKey: "admin.nav.customerUsers",
    descriptionKey: "admin.pageDescriptions.customerUsers",
    keywords: [
      "kunde",
      "customer",
      "kundenbenutzer",
      "kontakt",
      "contact",
      "avatar",
      "kundenbilder",
      "gravatar",
      "favicon",
      "logo",
    ],
  },
  {
    slug: "customer-companies",
    route: "/admin/customer-companies",
    group: "access",
    nameKey: "admin.nav.customerCompanies",
    descriptionKey: "admin.pageDescriptions.customerCompanies",
    keywords: ["kunde", "unternehmen", "firma", "company", "organisation"],
  },
  {
    slug: "customer-user-customers",
    route: "/admin/customer-user-customers",
    group: "access",
    nameKey: "admin.nav.customerUserCustomers",
    descriptionKey: "admin.pageDescriptions.customerUserCustomers",
    keywords: ["zuordnung", "unternehmenszuordnung", "assignment"],
  },
  {
    slug: "customer-user-groups",
    route: "/admin/customer-user-groups",
    group: "access",
    nameKey: "admin.nav.customerUserGroups",
    descriptionKey: "admin.pageDescriptions.customerUserGroups",
    keywords: ["zuordnung", "customer groups", "assignment"],
  },
  {
    slug: "identity-providers",
    route: "/admin/identity-providers",
    group: "access",
    nameKey: "admin.nav.identityProviders",
    descriptionKey: "admin.pageDescriptions.identityProviders",
    keywords: [
      "sso",
      "saml",
      "oidc",
      "ldap",
      "active directory",
      "entra",
      "azure",
      "google",
      "keycloak",
      "authentik",
      "adfs",
      "identitätsanbieter",
      "anmeldung",
      "single sign-on",
    ],
  },
  {
    slug: "auth-config",
    route: "/admin/auth-config",
    group: "access",
    nameKey: "admin.nav.authConfig",
    descriptionKey: "admin.pageDescriptions.authConfig",
    keywords: [
      "2fa",
      "totp",
      "passkey",
      "webauthn",
      "sso",
      "kerberos",
      "ldap",
      "authentifizierung",
      "anmeldung",
    ],
  },

  // ── Tickets & Workflow ──────────────────────────────────────────────
  {
    slug: "queues",
    route: "/admin/queues",
    group: "tickets",
    nameKey: "admin.nav.queues",
    descriptionKey: "admin.pageDescriptions.queues",
    keywords: ["postfach", "queue", "mailbox"],
  },
  {
    slug: "cmdb-classes",
    route: "/admin/cmdb/classes",
    group: "tickets",
    nameKey: "admin.nav.cmdbClasses",
    descriptionKey: "admin.pageDescriptions.cmdbClasses",
    keywords: ["cmdb", "configuration item", "ci", "klasse", "klassen", "asset", "inventar", "klassendefinition"],
  },
  {
    slug: "cmdb-catalog",
    route: "/admin/cmdb/catalog",
    group: "tickets",
    nameKey: "admin.nav.cmdbCatalog",
    descriptionKey: "admin.pageDescriptions.cmdbCatalog",
    keywords: ["cmdb", "configuration item", "ci", "auswahlliste", "katalog", "general catalog", "asset", "inventar"],
  },
  {
    slug: "queue-templates",
    route: "/admin/queue-templates",
    group: "tickets",
    nameKey: "admin.nav.queueTemplates",
    descriptionKey: "admin.pageDescriptions.queueTemplates",
    keywords: ["vorlagen", "zuordnung", "templates", "queue templates"],
  },
  {
    slug: "queue-auto-responses",
    route: "/admin/queue-auto-responses",
    group: "tickets",
    nameKey: "admin.nav.queueAutoResponses",
    descriptionKey: "admin.pageDescriptions.queueAutoResponses",
    keywords: ["autoantwort", "zuordnung", "auto response", "queue"],
  },
  {
    slug: "queue-variables",
    route: "/admin/queue-variables",
    group: "tickets",
    nameKey: "admin.nav.queueVariables",
    descriptionKey: "admin.pageDescriptions.queueVariables",
    keywords: ["platzhalter", "variablen", "placeholder", "smart tags"],
  },
  {
    slug: "queue-customer-links",
    route: "/admin/queue-customer-links",
    group: "tickets",
    nameKey: "admin.nav.queueCustomerLinks",
    descriptionKey: "admin.pageDescriptions.queueCustomerLinks",
    keywords: ["kundenlink", "customer link", "netadmin", "extern", "diagnose"],
  },
  {
    slug: "states",
    route: "/admin/states",
    group: "tickets",
    nameKey: "admin.nav.states",
    descriptionKey: "admin.pageDescriptions.states",
    keywords: ["status", "zustand", "ticket status"],
  },
  {
    slug: "priorities",
    route: "/admin/priorities",
    group: "tickets",
    nameKey: "admin.nav.priorities",
    descriptionKey: "admin.pageDescriptions.priorities",
    keywords: ["priorität", "priority", "dringlichkeit"],
  },
  {
    slug: "types",
    route: "/admin/types",
    group: "tickets",
    nameKey: "admin.nav.types",
    descriptionKey: "admin.pageDescriptions.types",
    keywords: ["ticket type", "typ", "tickettyp"],
  },
  {
    slug: "services",
    route: "/admin/services",
    group: "tickets",
    nameKey: "admin.nav.services",
    descriptionKey: "admin.pageDescriptions.services",
    keywords: ["service", "dienst", "servicekatalog"],
  },
  {
    slug: "slas",
    route: "/admin/slas",
    group: "tickets",
    nameKey: "admin.nav.slas",
    descriptionKey: "admin.pageDescriptions.slas",
    keywords: ["sla", "service level", "reaktionszeit"],
  },
  {
    slug: "dynamic-fields",
    route: "/admin/dynamic-fields",
    group: "tickets",
    nameKey: "admin.nav.dynamicFields",
    descriptionKey: "admin.pageDescriptions.dynamicFields",
    keywords: ["custom fields", "freie felder", "metadaten"],
  },
  {
    slug: "acl",
    route: "/admin/acl",
    group: "tickets",
    nameKey: "admin.nav.acl",
    descriptionKey: "admin.pageDescriptions.acl",
    keywords: ["access control", "zugriffskontrolle", "berechtigung", "permissions"],
  },
  {
    slug: "ticket-attribute-relations",
    route: "/admin/ticket-attribute-relations",
    group: "tickets",
    nameKey: "admin.nav.ticketAttributeRelations",
    descriptionKey: "admin.pageDescriptions.ticketAttributeRelations",
    keywords: [
      "attribute relations",
      "ticket attribute",
      "abhängigkeiten",
      "service queue",
      "csv",
      "znuny",
    ],
  },
  {
    slug: "processes",
    route: "/admin/processes",
    group: "tickets",
    nameKey: "admin.nav.processes",
    descriptionKey: "admin.pageDescriptions.processes",
    keywords: ["workflow", "prozess", "process management", "bpmn", "designer", "prozess bearbeiten", "import"],
  },

  // ── Kommunikation & Vorlagen ───────────────────────────────────────
  {
    slug: "templates",
    route: "/admin/templates",
    group: "communication",
    nameKey: "admin.nav.templates",
    descriptionKey: "admin.pageDescriptions.templates",
    keywords: ["vorlagen", "antwortbausteine", "canned response", "template"],
  },
  {
    slug: "template-attachments",
    route: "/admin/template-attachments",
    group: "communication",
    nameKey: "admin.nav.templateAttachments",
    descriptionKey: "admin.pageDescriptions.templateAttachments",
    keywords: ["anhänge", "zuordnung", "template attachments"],
  },
  {
    slug: "attachments",
    route: "/admin/attachments",
    group: "communication",
    nameKey: "admin.nav.attachments",
    descriptionKey: "admin.pageDescriptions.attachments",
    keywords: ["dateien", "anhänge", "upload", "files"],
  },
  {
    slug: "signatures",
    route: "/admin/signatures",
    group: "communication",
    nameKey: "admin.nav.signatures",
    descriptionKey: "admin.pageDescriptions.signatures",
    keywords: ["signatur", "unterschrift", "email signature"],
  },
  {
    slug: "salutations",
    route: "/admin/salutations",
    group: "communication",
    nameKey: "admin.nav.salutations",
    descriptionKey: "admin.pageDescriptions.salutations",
    keywords: ["anrede", "grußformel", "greeting"],
  },
  {
    slug: "auto-responses",
    route: "/admin/auto-responses",
    group: "communication",
    nameKey: "admin.nav.autoResponses",
    descriptionKey: "admin.pageDescriptions.autoResponses",
    keywords: ["autoantwort", "abwesenheit", "auto reply", "vacation"],
  },
  {
    slug: "mail-outbound",
    route: "/admin/mail-outbound",
    group: "communication",
    nameKey: "admin.nav.mailOutbound",
    descriptionKey: "admin.pageDescriptions.mailOutbound",
    keywords: ["smtp", "mail", "versand", "absender", "outbound", "email"],
  },
  {
    slug: "pgp",
    route: "/admin/pgp",
    group: "communication",
    nameKey: "admin.nav.pgp",
    descriptionKey: "admin.pageDescriptions.pgp",
    keywords: ["pgp", "gpg", "gnupg", "openpgp", "schlüssel", "key", "signatur", "verschlüsselung", "encryption"],
  },
  {
    slug: "smime",
    route: "/admin/smime",
    group: "communication",
    nameKey: "admin.nav.smime",
    descriptionKey: "admin.pageDescriptions.smime",
    keywords: ["s/mime", "smime", "zertifikat", "certificate", "x509", "signatur", "verschlüsselung", "encryption"],
  },
  {
    slug: "mail-accounts",
    route: "/admin/mail-accounts",
    group: "communication",
    nameKey: "admin.nav.mailAccounts",
    descriptionKey: "admin.pageDescriptions.mailAccounts",
    keywords: ["imap", "pop3", "mailbox", "postfach", "mail account", "eingehend", "inbound"],
  },
  {
    slug: "telegram",
    route: "/admin/telegram",
    group: "communication",
    nameKey: "admin.nav.telegram",
    descriptionKey: "admin.pageDescriptions.telegram",
    keywords: ["telegram", "bot", "messenger", "kanal", "channel", "chat"],
  },
  {
    slug: "webchat",
    route: "/admin/webchat",
    group: "communication",
    nameKey: "admin.nav.webchat",
    descriptionKey: "admin.pageDescriptions.webchat",
    keywords: ["webchat", "chat", "live-chat", "widget", "website", "einbetten", "embed"],
  },
  {
    slug: "oauth2-tokens",
    route: "/admin/oauth2-tokens",
    group: "communication",
    nameKey: "admin.nav.oauth2Tokens",
    descriptionKey: "admin.pageDescriptions.oauth2Tokens",
    keywords: [
      "oauth",
      "oauth2",
      "xoauth2",
      "microsoft",
      "google",
      "exchange",
      "token",
      "mail auth",
    ],
  },
  {
    slug: "subject-config",
    route: "/admin/subject-config",
    group: "communication",
    nameKey: "admin.nav.subjectConfig",
    descriptionKey: "admin.pageDescriptions.subjectConfig",
    keywords: ["betreff", "hook", "subject", "referenznummer", "ticket number"],
  },
  {
    slug: "postmaster-filters",
    route: "/admin/postmaster-filters",
    group: "communication",
    nameKey: "admin.nav.postmasterFilters",
    descriptionKey: "admin.pageDescriptions.postmasterFilters",
    keywords: ["filter", "eingang", "email rules", "x-header"],
  },
  {
    slug: "system-addresses",
    route: "/admin/system-addresses",
    group: "communication",
    nameKey: "admin.nav.systemAddresses",
    descriptionKey: "admin.pageDescriptions.systemAddresses",
    keywords: ["system address", "from", "absender", "email address"],
  },

  // ── Automatisierung ──────────────────────────────────────────────────
  {
    slug: "generic-agent-jobs",
    route: "/admin/generic-agent-jobs",
    group: "automation",
    nameKey: "admin.nav.genericAgentJobs",
    descriptionKey: "admin.pageDescriptions.genericAgentJobs",
    keywords: ["automatisierung", "jobs", "cron", "genericagent", "scheduled tasks"],
  },
  {
    slug: "notification-events",
    route: "/admin/notification-events",
    group: "automation",
    nameKey: "admin.nav.notificationEvents",
    descriptionKey: "admin.pageDescriptions.notificationEvents",
    keywords: ["notification", "benachrichtigung", "events", "email notify"],
  },
  {
    slug: "webhooks",
    route: "/admin/webhooks",
    group: "automation",
    nameKey: "admin.nav.webhooks",
    descriptionKey: "admin.pageDescriptions.webhooks",
    keywords: ["integration", "callback", "http hook"],
  },
  {
    slug: "webservices",
    route: "/admin/webservices",
    group: "automation",
    nameKey: "admin.nav.webservices",
    descriptionKey: "admin.pageDescriptions.webservices",
    keywords: [
      "webservice", "genericinterface", "gi", "invoker", "requester", "debugger",
      "schnittstelle", "soap", "rest", "integration",
    ],
  },
  {
    slug: "itsm",
    route: "/admin/itsm",
    group: "tickets",
    nameKey: "admin.nav.itsm",
    descriptionKey: "admin.pageDescriptions.itsm",
    keywords: [
      "itsm", "cmdb", "konfigurationselemente", "ci", "prioritätsmatrix", "priority matrix", "kritikalität",
      "auswirkung", "criticality", "impact", "incident", "störung", "problem", "großstörung", "major incident",
    ],
  },
  {
    slug: "itsm-link-types",
    route: "/admin/itsm/link-types",
    group: "tickets",
    nameKey: "admin.nav.itsmLinkTypes",
    descriptionKey: "admin.pageDescriptions.itsmLinkTypes",
    keywords: ["verknüpfungstypen", "verknüpfung", "link types", "links", "linktyp", "itsm", "propagation"],
  },
  {
    slug: "itsm-change",
    route: "/admin/itsm/change",
    group: "tickets",
    nameKey: "admin.nav.itsmChange",
    descriptionKey: "admin.pageDescriptions.itsmChange",
    keywords: [
      "change", "changes", "cab", "freeze", "sperrzeitraum", "vorlage", "risiko", "rfc", "änderung",
      "risikofragen", "change-archiv", "standard-change",
    ],
  },
  {
    slug: "itsm-import",
    route: "/admin/itsm-import",
    group: "tickets",
    nameKey: "admin.nav.itsmImport",
    descriptionKey: "admin.pageDescriptions.itsmImport",
    keywords: ["itsm", "cmdb", "znuny", "import", "übernehmen", "umstellen", "ci", "konfigurationselemente"],
  },
  {
    slug: "settings-itsm",
    route: "/admin/settings/itsm",
    group: "tickets",
    nameKey: "admin.settings.itsm.title",
    descriptionKey: "admin.pageDescriptions.settingsItsm",
    keywords: ["itsm", "ticket-typ", "workaround", "bekannter fehler", "known error", "znuny-adresse"],
  },
  {
    slug: "settings-itsm-change",
    route: "/admin/settings/itsm_change",
    group: "tickets",
    nameKey: "admin.settings.itsmChange.title",
    descriptionKey: "admin.pageDescriptions.settingsItsmChange",
    keywords: ["change", "change-manager", "cab", "risikostufe", "freigabe", "notfall-change", "rfc"],
  },
  {
    slug: "approvals",
    route: "/admin/approvals",
    group: "tickets",
    nameKey: "admin.nav.approvals",
    descriptionKey: "admin.pageDescriptions.approvals",
    keywords: ["freigabe", "freigaben", "genehmigung", "approval", "approvals", "zurückziehen", "überfällig"],
  },
  {
    slug: "approval-templates",
    route: "/admin/approvals/templates",
    group: "tickets",
    nameKey: "admin.nav.approvalsTemplates",
    descriptionKey: "admin.pageDescriptions.approvalsTemplates",
    keywords: ["freigabe", "vorlage", "freigabevorlage", "template", "stufen", "quorum", "mehrheit", "frist"],
  },
  {
    slug: "approval-lists",
    route: "/admin/approvals/lists",
    group: "tickets",
    nameKey: "admin.nav.approvalsLists",
    descriptionKey: "admin.pageDescriptions.approvalsLists",
    keywords: ["freigabe", "freigabegruppe", "gruppe", "cab", "liste", "approver", "freigeber"],
  },
  {
    slug: "approval-delegations",
    route: "/admin/approvals/delegations",
    group: "tickets",
    nameKey: "admin.nav.approvalsDelegations",
    descriptionKey: "admin.pageDescriptions.approvalsDelegations",
    keywords: ["freigabe", "vertretung", "urlaub", "delegation", "stellvertreter", "substitute"],
  },
  {
    slug: "settings-approvals",
    route: "/admin/settings/approvals",
    group: "tickets",
    nameKey: "admin.settings.approvals.title",
    descriptionKey: "admin.pageDescriptions.settingsApprovals",
    keywords: ["freigabe", "erinnerung", "e-mail-link", "kundenfreigabe", "approvals", "mcp", "frist"],
  },
  {
    slug: "api-keys",
    route: "/admin/api-keys",
    group: "automation",
    nameKey: "admin.nav.apiKeys",
    descriptionKey: "admin.pageDescriptions.apiKeys",
    keywords: ["api", "token", "schlüssel", "zugriffsschlüssel", "integration"],
  },
  {
    slug: "ai",
    route: "/admin/ai",
    group: "ai",
    nameKey: "admin.nav.ai",
    descriptionKey: "admin.pageDescriptions.ai",
    keywords: ["ki", "ai", "llm", "assistent", "autoreply", "einstellungen"],
  },
  {
    slug: "ai-providers",
    route: "/admin/ai/providers",
    group: "ai",
    nameKey: "admin.nav.aiProviders",
    descriptionKey: "admin.pageDescriptions.aiProviders",
    keywords: ["ki", "llm", "provider", "openai", "zugang", "api-key", "budget"],
  },
  {
    slug: "ai-models",
    route: "/admin/ai/models",
    group: "ai",
    nameKey: "admin.nav.aiModels",
    descriptionKey: "admin.pageDescriptions.aiModels",
    keywords: ["ki", "llm", "modell", "profil", "aufgabe", "fallback", "ausweichmodell", "vision"],
  },
  {
    slug: "ai-mcp",
    route: "/admin/ai/mcp",
    group: "ai",
    nameKey: "admin.nav.aiMcp",
    descriptionKey: "admin.pageDescriptions.aiMcp",
    keywords: ["mcp", "tools", "werkzeuge", "model context protocol"],
  },
  {
    slug: "ai-queues",
    route: "/admin/ai/queues",
    group: "ai",
    nameKey: "admin.nav.aiQueues",
    descriptionKey: "admin.pageDescriptions.aiQueues",
    keywords: ["ki", "queue", "autonomie", "eskalation", "policy"],
  },
  {
    slug: "ai-audit",
    route: "/admin/ai/audit",
    group: "ai",
    nameKey: "admin.nav.aiAudit",
    descriptionKey: "admin.pageDescriptions.aiAudit",
    keywords: ["ki", "audit", "protokoll", "log", "llm", "pii", "anfragen"],
  },
  {
    slug: "ai-acl",
    route: "/admin/ai/acl",
    group: "ai",
    nameKey: "admin.nav.aiAcl",
    descriptionKey: "admin.pageDescriptions.aiAcl",
    keywords: ["ki", "acl", "zugriff", "limits", "budget", "berechtigung", "kontingent"],
  },

  // ── System & Betrieb ─────────────────────────────────────────────────
  // System-Info (overview) before Dienste (control) so operators land on
  // the health board first.
  {
    slug: "system",
    route: "/admin/system",
    group: "system",
    nameKey: "admin.nav.systemInfo",
    descriptionKey: "admin.pageDescriptions.systemInfo",
    keywords: [
      "system",
      "info",
      "status",
      "version",
      "container",
      "docker",
      "healthcheck",
      "auslastung",
      "cpu",
      "ram",
      "speicher",
      "diagnose",
      "übersicht",
    ],
  },
  {
    slug: "daemons",
    route: "/admin/daemons",
    group: "system",
    nameKey: "admin.nav.daemons",
    descriptionKey: "admin.pageDescriptions.daemons",
    keywords: [
      "daemon",
      "cron",
      "postmaster",
      "eskalation",
      "imap",
      "hintergrund",
      "worker",
      "dienste",
      "services",
    ],
  },
  {
    slug: "plugins",
    route: "/admin/plugins",
    group: "system",
    nameKey: "admin.nav.plugins",
    descriptionKey: "admin.pageDescriptions.plugins",
    keywords: ["erweiterungen", "plugins", "add-ons", "module", "pakete", "opm", "sicherer modus", "safe mode", "paketquelle", "repository", "installieren", "signatur", "aktualisierung"],
  },
  {
    slug: "settings-jobs",
    route: "/admin/settings/jobs",
    group: "system",
    nameKey: "admin.settings.jobs.title",
    descriptionKey: "admin.pageDescriptions.settingsJobs",
    keywords: ["hintergrundaufgaben", "export", "aufgaben", "jobs", "background", "retention", "aufbewahrung"],
  },
  {
    slug: "reporting",
    route: "/admin/reporting",
    group: "system",
    nameKey: "admin.nav.reporting",
    descriptionKey: "admin.pageDescriptions.reporting",
    keywords: ["berichte", "statistik", "auswertung", "reports", "reporting", "neu aufbauen", "rebuild", "backfill"],
  },
  {
    slug: "settings-reporting",
    route: "/admin/settings/reporting",
    group: "system",
    nameKey: "admin.settings.reporting.title",
    descriptionKey: "admin.pageDescriptions.settingsReporting",
    keywords: ["berichte", "zeitzone", "cache", "export", "zugriff", "statistik", "reports", "sla"],
  },
  {
    slug: "gdpr",
    route: "/admin/gdpr",
    group: "system",
    nameKey: "admin.nav.gdpr",
    descriptionKey: "admin.pageDescriptions.gdpr",
    keywords: ["gdpr", "dsgvo", "löschen", "auskunft", "anonymisierung", "datenschutz", "privacy"],
  },
  {
    slug: "mail-log",
    route: "/admin/mail-log",
    group: "system",
    nameKey: "admin.nav.mailLog",
    descriptionKey: "admin.pageDescriptions.mailLog",
    keywords: ["kommunikationsprotokoll", "mail log", "versandprotokoll", "zustellung"],
  },
  {
    slug: "customer-fields",
    route: "/admin/customer-fields",
    group: "system",
    nameKey: "admin.nav.customerFields",
    descriptionKey: "admin.pageDescriptions.customerFields",
    keywords: ["platzhalter", "variablen", "customer felder", "placeholder"],
  },
  {
    slug: "dashboard-templates",
    route: "/admin/dashboard-templates",
    group: "system",
    nameKey: "admin.nav.dashboardTemplates",
    descriptionKey: "admin.pageDescriptions.dashboardTemplates",
    keywords: ["startseite", "vorlage", "dashboard", "widgets", "rolle"],
  },
  {
    slug: "dashboard-kiosk-links",
    route: "/admin/dashboard-kiosk-links",
    group: "system",
    nameKey: "admin.nav.dashboardKioskLinks",
    descriptionKey: "admin.pageDescriptions.dashboardKioskLinks",
    keywords: ["wallboard", "kiosk", "tv", "bildschirm", "link", "anzeigetafel"],
  },
];

export function adminPagesByGroup(group: AdminPageGroup): AdminPageEntry[] {
  return ADMIN_PAGES.filter((page) => page.group === group);
}

/**
 * Ranks admin pages for the ⌘K palette: a name hit outranks a keyword hit,
 * which outranks a description hit. All matches are case-insensitive
 * substrings. `t` resolves the i18n keys so ranking follows the active
 * locale. An empty query returns the pages in registry order.
 */
export function rankAdminPages(
  pages: AdminPageEntry[],
  query: string,
  t: (key: string) => string,
): AdminPageEntry[] {
  const term = query.trim().toLowerCase();
  if (!term) return pages;

  const scored: { page: AdminPageEntry; score: number }[] = [];
  for (const page of pages) {
    const name = t(page.nameKey).toLowerCase();
    const description = t(page.descriptionKey).toLowerCase();
    let score = 0;
    if (name.includes(term)) {
      score = 3;
    } else if (page.keywords.some((keyword) => keyword.toLowerCase().includes(term))) {
      score = 2;
    } else if (description.includes(term)) {
      score = 1;
    }
    if (score > 0) scored.push({ page, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.map((s) => s.page);
}
